<script setup lang="ts">
// TODO we will want to refactor this into a proper form field input that can be used universally once the design has been nailed
import type { FormFieldInjectedOptions } from '@nuxt/ui';
import type { ComputedRef } from 'vue';
import type { GlobalCollectionId, GlobalPrimitiveFieldId } from '#layers/data-model';
import { formFieldInjectionKey } from '@nuxt/ui/composables/useFormField';
import { computed, provide, ref } from 'vue';
import { useProjectSchemaStore } from '#imports';
import { ButtonAdd, ButtonRemove, useFormField } from '#layers/ui';

const props = defineProps<{
	collectionId: GlobalCollectionId;
	disabled?: boolean;
}>();

const fieldIds = defineModel<GlobalPrimitiveFieldId[]>({ default: () => [] });

const commandPaletteOpen = ref(false);

const { getCollection } = useProjectSchemaStore();

const resolvedCollection = computed(() => getCollection(props.collectionId));

const { color, emitFormChange, emitFormBlur, emitFormFocus, emitFormInput } = useFormField();

const fields = computed(() =>
	resolvedCollection.value?.primitiveFields
		.toSorted((a, b) => a.dbName.localeCompare(b.dbName)) ?? []);

const dropdownItems = computed(() => fields.value.filter((field) => !fieldIds.value.includes(field.id))
	.map((field) => ({
		label: field.dbName,
		onSelect: () => {
			commandPaletteOpen.value = false;
			fieldIds.value = [...fieldIds.value, field.id];
			emitFormChange();
			emitFormInput();
			emitFormBlur();
		},
	})));

const listItems = computed({
	get() {
		return fieldIds.value
			.map((fieldId) => fields.value.find((field) => field.id === fieldId))
			.filter((field) => field !== undefined)
			.map((field) => ({
				id: field.id,
				label: field.dbName,
				description: field.apiName !== field.dbName ? field.apiName : undefined,
			}));
	},
	set(value) {
		fieldIds.value = value.map((item) => item.id);
	},
});

function handleRemove(fieldId: GlobalPrimitiveFieldId) {
	fieldIds.value = fieldIds.value.filter((id) => id !== fieldId);
	emitFormChange();
	emitFormInput();
	emitFormBlur();
}

// make sure that downstream inputs don't assume they are the top level form field
provide(formFieldInjectionKey, undefined as unknown as ComputedRef<FormFieldInjectedOptions<any>>);
</script>

<template>
	<div
		class="divide-y rounded-md border"
		:class="{
			'border-accented': color !== 'error',
			'border-error': color === 'error',
		}"
	>
		<div class="flex h-13 items-center justify-between gap-x-3 px-6">
			<span class="text-xs font-medium" :class="{ 'text-muted': fieldIds.length === 0 }">
				{{ $t('common.columns_selected', fieldIds.length) }}
			</span>
			<DCommandPopover
				v-model:open="commandPaletteOpen"

				:groups="[{ id: 'root', items: dropdownItems }]"
				:placeholder="$t('common.search')"
				:ui="{
					item: 'font-mono font-medium',
					input: 'font-mono font-medium',
				}"
			>
				<ButtonAdd
					class="-ml-2"
					size="sm"
					variant="ghost"
					:square="fieldIds.length > 0"
					:label="fieldIds.length === 0 ? $t('pages.data_model.add_column') : undefined"
					:disabled="dropdownItems.length === 0 || disabled"
					@focus="emitFormFocus"
				/>
			</DCommandPopover>
		</div>
		<DList
			v-if="listItems.length > 0"
			v-model:items="listItems"
			sortable
			class="rounded-t-none border-none"
			:disabled
			:ui="{
				item: { base: 'font-mono font-medium' },
			}"
		>
			<template #item-trailing="{ item }">
				<ButtonRemove
					class="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
					with-leading-icon
					:trash-bin="false"
					:disabled
					square
					@click="handleRemove(item.id)"
				/>
			</template>
		</DList>
	</div>
</template>
